import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowRight, Sparkles, Loader2 } from 'lucide-react';
import { readableApiError } from '../utils/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Epic full-bleed event imagery (Unsplash CDN — royalty-free).
// Picked for cinematic crowd + stage-light energy.
const HERO_IMAGE =
  'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?auto=format&fit=crop&w=1600&q=80';

/**
 * /organizations/apply — split-screen organizer signup, inspired by
 * the TicketLeap admin register flow. Left half is an epic hero
 * image with the Eventism wordmark; right half is a tight form.
 *
 * Posts to POST /api/organizations/register and lands the new user
 * straight in /organization/dashboard with a populated JWT.
 */
export default function OrganizationApply() {
  const [form, setForm] = useState({
    legal_name: '',
    display_name: '',
    email: '',
    password: '',
    phone: '',
    primary_city: 'denver',
    bio: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.legal_name || form.legal_name.length < 2) {
      toast.error('Organization name is required');
      return;
    }
    if (!form.email.includes('@')) {
      toast.error('Valid email is required');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      // If the visitor is already signed in (most often as a fan who just
      // bought a ticket and now wants to host their own), forward the
      // bearer token so the backend can auto-upgrade their account without
      // requiring them to retype their password.
      const existingToken = localStorage.getItem('eventism_token');
      const headers = existingToken ? { Authorization: `Bearer ${existingToken}` } : {};
      const res = await axios.post(`${API}/organizations/register`, {
        legal_name: form.legal_name,
        display_name: form.display_name || form.legal_name,
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone || undefined,
        primary_city: form.primary_city || undefined,
      }, { headers });
      // Persist JWT under the key AuthContext reads from on mount, then
      // do a hard navigation so AuthProvider re-initializes with the new
      // token and the dashboard route-guard sees us as authenticated.
      localStorage.setItem('eventism_token', res.data.token);
      if (form.bio) {
        try {
          await axios.put(
            `${API}/organizations/me`,
            { bio: form.bio },
            { headers: { Authorization: `Bearer ${res.data.token}` } },
          );
        } catch (_) { /* non-fatal */ }
      }
      toast.success(res.data?.upgraded
        ? 'Welcome back — your account has been upgraded to organizer.'
        : 'Welcome to Eventism — your organizer account is live.');
      window.location.href = '/organization/dashboard';
    } catch (err) {
      toast.error(readableApiError(err, 'Sign-up failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background grid lg:grid-cols-2"
      data-testid="organization-apply-page"
    >
      {/* ============ LEFT — Epic hero image ============ */}
      <aside
        className="relative hidden lg:block overflow-hidden"
        data-testid="apply-hero-image"
      >
        <img
          src={HERO_IMAGE}
          alt="Live event with stage lights and crowd"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        {/* Cinematic overlays for legibility + brand identity */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/30 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(217,70,239,0.18),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(245,158,11,0.15),transparent_50%)]" />

        {/* Top brand */}
        <Link
          to="/"
          className="absolute top-8 left-8 flex items-center gap-2 z-10"
          data-testid="apply-hero-logo"
        >
          <Sparkles className="h-5 w-5 text-amber-300" />
          <span className="text-white font-bold tracking-tight text-lg font-['Space_Grotesk']">
            Eventism
          </span>
        </Link>

        {/* Bottom wordmark */}
        <div className="absolute bottom-10 left-10 right-10 z-10">
          <h2 className="text-white font-bold font-['Space_Grotesk'] leading-[0.95] text-5xl xl:text-6xl">
            Where{' '}
            <span className="bg-gradient-to-r from-amber-300 via-fuchsia-300 to-indigo-300 bg-clip-text text-transparent">
              great events
            </span>
            <br />
            get sold.
          </h2>
        </div>
      </aside>

      {/* ============ RIGHT — Signup form ============ */}
      <main
        className="flex items-center justify-center px-6 sm:px-12 py-16"
        data-testid="apply-form-pane"
      >
        <div className="w-full max-w-md space-y-5">
          {/* Mobile-only brand (image hides on small screens) */}
          <Link to="/" className="lg:hidden flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-400" />
            <span className="font-bold tracking-tight text-lg font-['Space_Grotesk']">
              Eventism
            </span>
          </Link>

          <div className="space-y-1.5">
            <h1
              className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] leading-tight"
              data-testid="apply-h1"
            >
              Create your account
            </h1>
            <p className="text-sm text-muted-foreground">
              Already have one?{' '}
              <Link to="/login" className="text-amber-400 hover:text-amber-300 font-medium">
                Sign in
              </Link>
            </p>
          </div>

          <Card className="border-border bg-card/60">
            <CardContent className="p-5 sm:p-6">
              <form onSubmit={submit} className="space-y-3" data-testid="apply-form">
                <Field label="Organization legal name *">
                  <Input
                    value={form.legal_name}
                    onChange={onChange('legal_name')}
                    placeholder="Acme Events LLC"
                    data-testid="apply-legal-name"
                    required
                  />
                </Field>
                <Field label="Display name (what fans see)">
                  <Input
                    value={form.display_name}
                    onChange={onChange('display_name')}
                    placeholder="Acme Events"
                    data-testid="apply-display-name"
                  />
                </Field>
                <Field label="Email *">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={onChange('email')}
                    placeholder="hello@acme.com"
                    data-testid="apply-email"
                    required
                  />
                </Field>
                <Field label="Password *">
                  <Input
                    type="password"
                    value={form.password}
                    onChange={onChange('password')}
                    placeholder="At least 8 characters"
                    data-testid="apply-password"
                    required
                  />
                  <p className="text-[10px] text-muted-foreground pt-1" data-testid="apply-password-hint">
                    Already have an Eventism fan account? Enter your existing
                    password and we&apos;ll upgrade it to an organizer account.
                  </p>
                </Field>
                <Field label="Phone">
                  <Input
                    value={form.phone}
                    onChange={onChange('phone')}
                    placeholder="555-0100"
                    data-testid="apply-phone"
                  />
                </Field>
                <Field label="Primary city">
                  <select
                    value={form.primary_city}
                    onChange={onChange('primary_city')}
                    className="w-full h-10 rounded-md border border-border bg-card px-3 text-sm"
                    data-testid="apply-city"
                  >
                    <option value="denver">Denver</option>
                    <option value="las-vegas">Las Vegas</option>
                    <option value="los-angeles">Los Angeles</option>
                    <option value="charlotte">Charlotte</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Tell us about your events (optional)">
                  <Textarea
                    value={form.bio}
                    onChange={onChange('bio')}
                    rows={2}
                    placeholder="What kind of events do you produce?"
                    data-testid="apply-bio"
                  />
                </Field>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0"
                  data-testid="apply-submit-btn"
                >
                  {submitting
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating account…</>
                    : <>Create my account <ArrowRight className="h-4 w-4 ml-2" /></>}
                </Button>

                <p className="text-[10px] text-muted-foreground text-center pt-1">
                  By creating an account you agree to our Terms &amp; Marketplace Policy.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] uppercase font-bold text-muted-foreground tracking-wide">
        {label}
      </span>
      {children}
    </label>
  );
}
